/**
 * Institution Service Types
 *
 * Types and interfaces for the institution-service endpoints
 * Simple CRUD operations for educational institutions
 */

// Base institution types
interface Establishment {
  id: string;
  name: string;
}

interface Institution {
  id: string;
  name: string;
  address: string;
  zipcode: string;
  city: string;
  state: string;
  status: 'ativo' | 'inativo';
  created_at?: string;
  updated_at?: string;
  establishments?: Establishment[];
}

// Request types
interface CreateInstitutionRequest {
  name: string;
  address: string;
  zipcode: string;
  city: string;
  state: string;
  status: 'ativo' | 'inativo';
}

interface UpdateInstitutionRequest {
  id: string;
  name: string;
  address: string;
  zipcode: string;
  city: string;
  state: string;
  status: 'ativo' | 'inativo';
}

interface InstitutionDetailsRequest {
  id: string;
}

// Response types
interface InstitutionListResponse {
  institutions: Institution[];
  total?: number;
  has_more?: boolean;
  offset?: number;
  next_offset?: number;
}

interface InstitutionDetailsResponse {
  institution: Institution;
}

// Search and filter types
interface InstitutionSearchParams {
  q?: string; // Search term for general search
  name?: string;
  city?: string;
  state?: string;
  status?: 'ativo' | 'inativo';
  limit?: number; // Page size (default: 20)
  offset?: number; // Page offset (default: 0)
}

// API Response wrapper
interface InstitutionApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Common parameters
interface InstitutionParams {
  institution_id: string;
}

// Status enum for type safety
enum InstitutionStatus {
  ACTIVE = 'ativo',
  INACTIVE = 'inativo',
}

// Brazilian states enum for validation
enum BrazilianStates {
  AC = 'AC', // Acre
  AL = 'AL', // Alagoas
  AP = 'AP', // Amapá
  AM = 'AM', // Amazonas
  BA = 'BA', // Bahia
  CE = 'CE', // Ceará
  DF = 'DF', // Distrito Federal
  ES = 'ES', // Espírito Santo
  GO = 'GO', // Goiás
  MA = 'MA', // Maranhão
  MT = 'MT', // Mato Grosso
  MS = 'MS', // Mato Grosso do Sul
  MG = 'MG', // Minas Gerais
  PA = 'PA', // Pará
  PB = 'PB', // Paraíba
  PR = 'PR', // Paraná
  PE = 'PE', // Pernambuco
  PI = 'PI', // Piauí
  RJ = 'RJ', // Rio de Janeiro
  RN = 'RN', // Rio Grande do Norte
  RS = 'RS', // Rio Grande do Sul
  RO = 'RO', // Rondônia
  RR = 'RR', // Roraima
  SC = 'SC', // Santa Catarina
  SP = 'SP', // São Paulo
  SE = 'SE', // Sergipe
  TO = 'TO', // Tocantins
}

// Validation types
interface InstitutionValidation {
  name: {
    required: boolean;
    minLength: number;
    maxLength: number;
  };
  address: {
    required: boolean;
    minLength: number;
    maxLength: number;
  };
  zipcode: {
    required: boolean;
    pattern: RegExp; // Brazilian zipcode pattern: XXXXX-XXX
  };
  city: {
    required: boolean;
    minLength: number;
    maxLength: number;
  };
  state: {
    required: boolean;
    validValues: BrazilianStates[];
  };
  status: {
    required: boolean;
    validValues: InstitutionStatus[];
  };
}

// Default validation rules
const INSTITUTION_VALIDATION_RULES: InstitutionValidation = {
  name: {
    required: true,
    minLength: 3,
    maxLength: 100,
  },
  address: {
    required: true,
    minLength: 10,
    maxLength: 200,
  },
  zipcode: {
    required: true,
    pattern: /^\d{5}-?\d{3}$/, // Accepts XXXXX-XXX or XXXXXXXX format
  },
  city: {
    required: true,
    minLength: 2,
    maxLength: 50,
  },
  state: {
    required: true,
    validValues: Object.values(BrazilianStates),
  },
  status: {
    required: true,
    validValues: Object.values(InstitutionStatus),
  },
};

// Utility types for forms and UI
interface InstitutionFormData {
  name: string;
  address: string;
  zipcode: string;
  city: string;
  state: BrazilianStates;
  status: InstitutionStatus;
}

interface InstitutionFormErrors {
  name?: string;
  address?: string;
  zipcode?: string;
  city?: string;
  state?: string;
  status?: string;
  general?: string;
}

// Sorting and pagination types
interface InstitutionSortOptions {
  field: 'name' | 'city' | 'state' | 'status' | 'created_at' | 'updated_at';
  direction: 'asc' | 'desc';
}

interface InstitutionPaginationParams {
  page: number;
  limit: number;
  sort?: InstitutionSortOptions;
}

interface PaginatedInstitutionResponse {
  institutions: Institution[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
  };
}

// Statistics and summary types
interface InstitutionStats {
  total: number;
  active: number;
  inactive: number;
  by_state: Record<string, number>;
  recent_additions: number; // Last 30 days
}

interface GetAllInstitutionsResponse {
  id: string;
  name: string;
  address: string;
  zipcode: string;
  city: string;
  state: string;
  status: string;
  created_date: string;
}

type GetAllInstitutionsListResponse = GetAllInstitutionsResponse[];

interface InstitutionDropdownListResponse {
  data: Institution[];
  total: number;
  limit: number;
  offset: number;
}

// Export all types for easy importing
export type {
  Establishment,
  Institution,
  CreateInstitutionRequest,
  UpdateInstitutionRequest,
  InstitutionDetailsRequest,
  InstitutionListResponse,
  InstitutionDetailsResponse,
  InstitutionSearchParams,
  InstitutionApiResponse,
  InstitutionParams,
  InstitutionFormData,
  InstitutionFormErrors,
  InstitutionSortOptions,
  InstitutionPaginationParams,
  PaginatedInstitutionResponse,
  InstitutionStats,
  InstitutionValidation,
  GetAllInstitutionsResponse,
  GetAllInstitutionsListResponse,
  InstitutionDropdownListResponse,
};

// Export enums
export { InstitutionStatus, BrazilianStates };

// Export validation rules
export { INSTITUTION_VALIDATION_RULES };
